/**
 * SaveIndicator — Fase 14.
 *
 * Componente visual del estado del autoguardado. Dos modos:
 *
 *   - Modo badge (status normal): muestra "Guardando…", "✓ Guardado" o
 *     "Error al guardar" como texto pequeño junto a los botones de navegación.
 *     Invisible cuando status === "idle" y no hay conflicto.
 *
 *   - Modo banner (conflicto): cuando saveConflictAtom es no-null, muestra
 *     un banner prominente con las opciones "Descartar mis cambios" y
 *     "Sobreescribir" para resolver el conflicto de versiones.
 *
 * Uso:
 *   const { resolveConflict } = useAutoSave();
 *   <SaveIndicator onResolveConflict={resolveConflict} />
 */

import { useAtomValue } from "jotai";
import styled from "styled-components";
import { saveConflictAtom, saveStatusAtom } from "../../state/atoms";
import { Button } from "./Button";

type Props = {
  onResolveConflict: (action: "discard" | "overwrite") => void;
};

export function SaveIndicator({ onResolveConflict }: Props) {
  const status = useAtomValue(saveStatusAtom);
  const conflict = useAtomValue(saveConflictAtom);

  // Conflicto activo — banner prominente (tiene prioridad sobre el badge).
  if (conflict !== null) {
    return (
      <ConflictBanner>
        <ConflictIcon>⚠️</ConflictIcon>
        <ConflictText>
          Otro tab guardó cambios más recientes. ¿Qué querés hacer con tus cambios locales?
        </ConflictText>
        <ConflictActions>
          <Button $variant="ghost" onClick={() => onResolveConflict("discard")}>
            Descartar mis cambios
          </Button>
          <Button $variant="primary" onClick={() => onResolveConflict("overwrite")}>
            Sobreescribir
          </Button>
        </ConflictActions>
      </ConflictBanner>
    );
  }

  // Badge de estado — solo visible cuando hay actividad.
  if (status === "idle") return null;

  return (
    <Badge $status={status}>
      {status === "saving" && "Guardando…"}
      {status === "saved" && "✓ Guardado"}
      {status === "error" && "Error al guardar"}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const ConflictBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  background: ${({ theme }) => theme.color.warningMuted};
  border: 1px solid ${({ theme }) => theme.color.warning};
  border-radius: ${({ theme }) => theme.radius.md};
  flex-wrap: wrap;
`;

const ConflictIcon = styled.span`
  font-size: ${({ theme }) => theme.font.size.lg};
  flex-shrink: 0;
`;

const ConflictText = styled.span`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  flex: 1;
  min-width: 200px;
`;

const ConflictActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  flex-shrink: 0;
`;

const Badge = styled.span<{ $status: "saving" | "saved" | "error" }>`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-family: ${({ theme }) => theme.font.mono};
  color: ${({ theme, $status }) =>
    $status === "saved"
      ? theme.color.success ?? theme.color.accent
      : $status === "error"
        ? theme.color.error
        : theme.color.textMuted};
  transition: opacity 0.2s ease;
`;
